import { useMemo, useState } from 'react'
import NextLink from 'next/link'
import clsx from 'clsx'
import { useQueryClient } from 'react-query'

import { Icon } from '@wartek-id/icon-v2'
import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'

import { PelaksanaanAppealRollbackDialog } from 'app/KinerjaGpkPage/components/PelaksanaanAppeal/PelaksanaanAppealRollbackDialog'
import { PelaksanaanAppealRequestDialog } from 'app/KinerjaGpkPage/components/PelaksanaanAppeal/PelaksanaanAppealRequestDialog'
import { PraktikKinerjaStepCard } from 'app/KinerjaShared/components/PraktikKinerjaStepCard'
import {
  PRAKTIK_KINERJA_STEPS,
  PRAKTIK_KINERJA_STEP_ORDER,
} from 'app/KinerjaGpkPage/constants/praktik-kinerja-steps'
import {
  API_PATH_PELAKSANAAN_PROGRESS,
  API_PATH_PELAKSANAAN_DOCUMENT_PREPARATION,
} from 'api/kinerja/useAPIKinerja'
import useAppealRollback from 'app/KinerjaGpkPage/hooks/use-appeal-rollback'
import { PelaksanaanAppealTargetPerilakuConfirmationDialog } from 'app/KinerjaGpkPage/components/PelaksanaanAppeal/PelaksanaanAppealTargetPerilakuConfirmationDialog'

import type { StepCardStatus } from 'app/KinerjaShared/components/PraktikKinerjaStepCard'
import type {
  TFollowUpEvaluationDocumentStates,
  TPelaksanaanAppealableStep,
  TStatePelaksanaan,
} from 'types/kinerja-pelaksanaan'

type PersiapanStepCardProps = {
  currentState?: TStatePelaksanaan
  latestFormState?: TFollowUpEvaluationDocumentStates
  appealState?: TPelaksanaanAppealableStep
}

const STEP: TStatePelaksanaan = 'DOCUMENT_PREPARATION'

export function PersiapanStepCard({
  appealState,
  currentState,
  latestFormState,
}: PersiapanStepCardProps) {
  const queryClient = useQueryClient()
  const { isAppealRollbackEnabled } = useAppealRollback()

  // ----------------------------------------------------
  // Dialog state
  // ----------------------------------------------------
  const [activeDialog, setActiveDialog] = useState<
    'CONFIRMATION' | 'ROLLBACK' | 'APPEAL' | 'NONE'
  >('NONE')

  // ----------------------------------------------------
  // Resolve step card status
  // ----------------------------------------------------
  const stepCardStatus: StepCardStatus = useMemo(() => {
    const currentStepIndex = PRAKTIK_KINERJA_STEP_ORDER.indexOf(currentState)
    const persiapanStepIndex = PRAKTIK_KINERJA_STEP_ORDER.indexOf(STEP)

    if (currentStepIndex > persiapanStepIndex) return 'completed'
    if (currentStepIndex === persiapanStepIndex) return 'active'

    return 'locked'
  }, [currentState])

  // ----------------------------------------------------
  // Derived values
  // ----------------------------------------------------
  const showCTA = stepCardStatus !== 'completed'
  const showAppealCTA =
    isAppealRollbackEnabled && stepCardStatus === 'completed'
  const isEditDisabled = !!appealState && appealState !== STEP
  const isAppealing = appealState === STEP

  // ----------------------------------------------------
  // Handlers
  // ----------------------------------------------------
  const handleClickEdit = () => {
    if (
      currentState === 'OBSERVATION_IMPLEMENTATION' &&
      latestFormState === 'NOT_CREATED'
    ) {
      // Immediately show rollback confirmation dialog
      // If confirmed, the form will immediately be set to DRAFT state
      return setActiveDialog('ROLLBACK')
    }

    // Otherwise, show appeal confirmation dialog
    return setActiveDialog('CONFIRMATION')
  }

  // ----------------------------------------------------
  // Step data
  // ----------------------------------------------------
  const stepData = PRAKTIK_KINERJA_STEPS[STEP]

  return (
    <>
      <PraktikKinerjaStepCard
        actions={
          showCTA ? (
            <NextLink href={stepData.getPath()} passHref>
              <Button
                size="lg"
                as="a"
                className="!inline-flex"
                type={undefined}
                color="blue"
              >
                {stepCardStatus === 'active'
                  ? latestFormState === 'DRAFT'
                    ? 'Lanjutkan'
                    : 'Lakukan'
                  : 'Cek rincian'}
              </Button>
            </NextLink>
          ) : showAppealCTA ? (
            isAppealing ? (
              <div className="flex gap-1 items-center py-3 animate-pulse">
                <Icon
                  color="informational"
                  className="motion-safe:animate-spin"
                >
                  hourglass_empty
                </Icon>
                <Text className="!text-blue-60" color="informational">
                  Perubahan sedang diajukan.
                </Text>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="lg"
                color="blue"
                disabled={isEditDisabled}
                className={clsx(
                  'gap-1 !bg-white text-nowrap',
                  isEditDisabled && '!text-blue-40'
                )}
                onClick={handleClickEdit}
              >
                <Icon color="inherit">edit_outline</Icon>
                Ubah Target Perilaku
              </Button>
            )
          ) : null
        }
        className="max-w-96"
        iconSrc={stepData.imagePath}
        status={stepCardStatus}
        title={
          <>
            Pemantauan Kinerja: <br /> Isi {stepData.title}
          </>
        }
        description="Diskusi dengan Tim Kinerja untuk target perilaku, upaya mempelajari, dan jadwal."
      />

      <PelaksanaanAppealRollbackDialog
        isOpen={activeDialog === 'ROLLBACK'}
        onClose={() => setActiveDialog('NONE')}
        onCancel={() => setActiveDialog('NONE')}
        step={STEP as TPelaksanaanAppealableStep}
        url={stepData.getPath()}
        onSuccess={() => {
          // Invalidate without auto-refetch to prevent double fetch
          // React Query will refetch when needed
          // and deduplicate if multiple components share the same query key
          queryClient.invalidateQueries(API_PATH_PELAKSANAAN_PROGRESS, {
            refetchActive: false,
            refetchInactive: false,
          })
          queryClient.invalidateQueries(
            API_PATH_PELAKSANAAN_DOCUMENT_PREPARATION,
            {
              refetchActive: false,
              refetchInactive: false,
            }
          )
        }}
      />
      <PelaksanaanAppealTargetPerilakuConfirmationDialog
        isOpen={activeDialog === 'CONFIRMATION'}
        onClose={() => setActiveDialog('NONE')}
        onCancel={() => setActiveDialog('NONE')}
        onConfirm={() => setActiveDialog('APPEAL')}
      />
      <PelaksanaanAppealRequestDialog
        isOpen={activeDialog === 'APPEAL'}
        currentStep={currentState}
        appealStep={STEP as TPelaksanaanAppealableStep}
        onClose={() => setActiveDialog('NONE')}
      />
    </>
  )
}
